import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DeviceDto } from '@gc/shared';

import { BulkConfirmDialog } from './BulkConfirmDialog';
import { StopRoundsLines, type StopRoundsEntry } from './StopRoundsFields';
import { bulkPause, pauseCountsRounds } from './bulkActions';
import { bulkNeedsTypedConfirm } from './deviceCapabilities';

/**
 * The selection bar's Pause: the board's typed confirm, plus the Device List
 * End's Round Type and Rounds for every paying round-priced sitting it ends
 * (OW19 review). Pause stops the sitting, so a console or a cinema sold by
 * the match is billed on the rounds counted here, one round by default, and
 * the cursor opens in the first one's Rounds, as the tile's End opens it.
 * Play's own free sittings and hourly machines are asked nothing.
 *
 * Mounted fresh each time Pause opens, so a cancelled answer is never the
 * next Pause's default.
 */
export function BulkPauseDialog({
  devices,
  onClose,
  onFinished,
}: {
  /** The selected devices, live from the board. */
  devices: readonly DeviceDto[];
  onClose: () => void;
  onFinished: () => void;
}) {
  const { t } = useTranslation();
  const [rounds, setRounds] = useState<ReadonlyMap<string, StopRoundsEntry>>(new Map());
  const roundPriced = devices.filter(pauseCountsRounds);

  return (
    <BulkConfirmDialog
      title={t('deviceBoard.bulkDialog.pauseTitle')}
      message={t('deviceBoard.bulkDialog.pauseMessage', { count: devices.length })}
      actionLabel={t('deviceBoard.bulk.pause')}
      deviceNames={devices.map((device) => device.name)}
      typedConfirm={bulkNeedsTypedConfirm('pause')}
      autoFocusTypedCount={roundPriced.length === 0}
      run={() => bulkPause(devices, rounds)}
      onClose={onClose}
      onFinished={onFinished}
    >
      <StopRoundsLines
        lines={roundPriced.map((device) => ({
          sessionId: device.currentSession!.id,
          deviceName: device.name,
          offersThreeBall: device.threeBallPriceMd != null,
        }))}
        rounds={rounds}
        onChange={(sessionId, next) =>
          setRounds((current) => new Map(current).set(sessionId, next))
        }
      />
    </BulkConfirmDialog>
  );
}
